import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom'
import { ROUTES } from '@/constants'
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
import { RootLayout } from '@/layouts/RootLayout'
import { LoadingSpinner } from '@/components/LoadingSpinner'

// ─────────────────────────────────────────────────────────────
// Lazy-loaded pages — each becomes its own JS chunk.
// This keeps the initial bundle tiny.
// ─────────────────────────────────────────────────────────────

const LandingPage    = lazy(() => import('@/pages/LandingPage'))
const LoginPage      = lazy(() => import('@/pages/LoginPage'))
const RegisterPage   = lazy(() => import('@/pages/RegisterPage'))
const HomePage       = lazy(() => import('@/pages/HomePage'))
const ExplorePage    = lazy(() => import('@/pages/ExplorePage'))
const SearchPage     = lazy(() => import('@/pages/SearchPage'))
const UploadPage     = lazy(() => import('@/pages/UploadPage'))
const PhotoDetailPage = lazy(() => import('@/pages/PhotoDetailPage'))
const ProfilePage    = lazy(() => import('@/pages/ProfilePage'))
const SettingsPage   = lazy(() => import('@/pages/SettingsPage'))
const NotFoundPage   = lazy(() => import('@/pages/NotFoundPage'))
const AboutPage      = lazy(() => import('@/pages/AboutPage'))

// ─────────────────────────────────────────────────────────────
// Page wrapper with loading fallback
// ─────────────────────────────────────────────────────────────

function PageSuspense() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[50vh] items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  )
}

// ─────────────────────────────────────────────────────────────
// Router definition
// ─────────────────────────────────────────────────────────────

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        element: <PageSuspense />,
        children: [
          // Public routes
          { index: true,                 element: <LandingPage /> },
          { path: ROUTES.LOGIN,          element: <LoginPage /> },
          { path: ROUTES.REGISTER,       element: <RegisterPage /> },
          { path: ROUTES.HOME,           element: <HomePage /> },
          { path: ROUTES.EXPLORE,        element: <ExplorePage /> },
          { path: ROUTES.SEARCH,         element: <SearchPage /> },
          { path: ROUTES.PHOTO,          element: <PhotoDetailPage /> },
          { path: ROUTES.PROFILE,        element: <ProfilePage /> },
          { path: ROUTES.ABOUT,          element: <AboutPage /> },

          // Protected routes (require auth)
          {
            path: ROUTES.UPLOAD,
            element: <ProtectedRoute><UploadPage /></ProtectedRoute>,
          },
          {
            path: ROUTES.SETTINGS,
            element: <ProtectedRoute><SettingsPage /></ProtectedRoute>,
          },

          // 404
          { path: ROUTES.NOT_FOUND, element: <NotFoundPage /> },
        ],
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
